import { API_URL } from '../config/config';

class ApiService {
  constructor() {
    this.baseURL = API_URL;
  }

  // Helper method to get headers with authentication token
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Origin': window.location.origin,
    };
    
    // Add Authorization header if token exists
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  // Generic HTTP methods
  async get(endpoint, params = {}, skipEmptyFilter = false) {
    const url = new URL(`${this.baseURL}${endpoint}`);
    
    // Filter out undefined, null, and empty string values (unless skipEmptyFilter is true)
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (skipEmptyFilter) {
        // Include all params even if empty (for optional params)
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value);
        }
      } else {
        // Original behavior - skip empty strings
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, value);
        }
      }
    });
    
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const jsonResponse = await response.json();
    return jsonResponse;
  }

  async post(endpoint, data) {
    try {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      mode: 'cors',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      let errorDetails = null;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
        errorDetails = errorData;
        console.error('API Error Response:', errorData);
      } catch (e) {
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
            try {
              errorDetails = JSON.parse(errorText);
            } catch (parseError) {
              errorDetails = { raw: errorText };
            }
            console.error('API Error Text:', errorText);
          }
        } catch (e2) {
          // Use default error message
        }
      }
      
      const error = new Error(errorMessage);
      error.response = response;
      error.details = errorDetails;
      throw error;
    }

      // Get response text first
      const text = await response.text();
      
      // If response is empty, check if it's a 201 Created (which might have empty body)
      if (!text || text.trim() === '') {
        if (response.status === 201) {
          console.warn('Empty response from API endpoint (201 Created):', endpoint);
          // For 201 Created, try to get location header or return success indicator
          const location = response.headers.get('location');
          if (location) {
            // Extract ID from location header if possible
            const match = location.match(/\/(\d+)$/);
            if (match) {
              return { bookingID: parseInt(match[1]), success: true };
            }
          }
          return { success: true };
        }
        console.warn('Empty response from API endpoint:', endpoint);
        return null;
      }
      
      // Try to parse as JSON
      try {
        return JSON.parse(text);
      } catch (e) {
        console.error('JSON parse error for endpoint:', endpoint);
        console.error('Error:', e.message);
        console.error('Response status:', response.status);
        console.error('Response text (first 500 chars):', text.substring(0, 500));
        console.error('Content-Type:', response.headers.get('content-type'));
        // If it's a 201 Created, the booking might have been created successfully
        if (response.status === 201 || response.status === 200) {
          console.warn('Response is not JSON but status is success. Booking may have been created.');
          // Try to extract booking ID from response if possible
          return { success: true, rawResponse: text };
        }
        throw new Error(`Failed to parse JSON response: ${e.message}. Response preview: ${text.substring(0, 200)}`);
      }
    } catch (error) {
      // Re-throw if it's already our custom error
      if (error.response) {
        throw error;
      }
      // Otherwise, wrap it
      console.error('Fetch error:', error);
      throw new Error(`Network error: ${error.message}`);
    }
  }

  async put(endpoint, data) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PUT',
      mode: 'cors',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        } catch (e2) {
          // Use default error message
        }
      }
      
      const error = new Error(errorMessage);
      error.response = response;
      throw error;
    }

    // Handle 204 No Content (common for PUT updates that don't return data)
    if (response.status === 204) {
      return { success: true, status: 204 };
    }

    // Get response text first
    const text = await response.text();
    
    // If response is empty, return success indicator
    if (!text || text.trim() === '') {
      return { success: true };
    }
    
    // Try to parse as JSON
    try {
      return JSON.parse(text);
    } catch (e) {
      // If status is success (200-299), return success indicator even if not JSON
      if (response.status >= 200 && response.status < 300) {
        console.warn('PUT response is not JSON but status is success:', endpoint);
        return { success: true, rawResponse: text };
      }
      throw new Error(`Failed to parse JSON response: ${e.message}`);
    }
  }

  async delete(endpoint) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'DELETE',
      mode: 'cors',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.ok;
  }

  async patch(endpoint, data) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PATCH',
      mode: 'cors',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }
}

// Vehicle API methods
export const vehicleApi = {
  // Get all vehicles with filtering and pagination
  getVehicles: (filters = {}, activeOnly = false) => {
    const api = new ApiService();
    const params = { ...filters };
    if (activeOnly) {
      params.activeOnly = true;
    }
    return api.get('/vehicle', params);
  },

  // Get active vehicles only
  getActiveVehicles: () => {
    const api = new ApiService();
    return api.get('/vehicle/available');
  },

  // Get vehicle by ID
  getVehicle: (id) => {
    const api = new ApiService();
    return api.get(`/vehicle/${id}`);
  },

  // Create new vehicle
  createVehicle: (vehicleData) => {
    const api = new ApiService();
    return api.post('/vehicle', vehicleData);
  },

  // Update existing vehicle
  updateVehicle: (id, vehicleData) => {
    const api = new ApiService();
    return api.put(`/vehicle/${id}`, vehicleData);
  },

  // Delete vehicle
  deleteVehicle: (id) => {
    const api = new ApiService();
    return api.delete(`/vehicle/${id}`);
  },

  // Toggle vehicle availability
  toggleAvailability: (id) => {
    const api = new ApiService();
    return api.patch(`/vehicle/${id}/toggle-availability`);
  },

  // Check if registration number is unique
  checkRegistrationNumber: (registrationNumber, excludeId = null) => {
    const api = new ApiService();
    const params = excludeId ? { excludeId } : {};
    return api.get(`/vehicle/check-registration/${registrationNumber}`, params);
  }
};

// Route API methods
export const routeApi = {
  // Get all routes with city information
  getRoutes: (activeOnly = false) => {
    const api = new ApiService();
    const params = activeOnly ? { activeOnly: true } : {};
    return api.get('/route', params);
  },

  // Get route by ID with city details
  getRoute: (id) => {
    const api = new ApiService();
    return api.get(`/route/${id}`);
  },

  // Create new route with city IDs
  createRoute: (routeData) => {
    const api = new ApiService();
    return api.post('/route', routeData);
  },

  // Update existing route with city IDs
  updateRoute: (id, routeData) => {
    const api = new ApiService();
    return api.put(`/route/${id}`, routeData);
  },

  // Delete route (soft delete)
  deleteRoute: (id) => {
    const api = new ApiService();
    return api.delete(`/route/${id}`);
  },

  // Toggle route status
  toggleStatus: (id) => {
    const api = new ApiService();
    return api.patch(`/route/${id}/toggle-status`);
  },

  // Get routes with city names (for display)
  getRoutesWithCities: (activeOnly = false) => {
    const api = new ApiService();
    const params = activeOnly ? { activeOnly: true, includeCities: true } : { includeCities: true };
    return api.get('/route', params);
  },

  // Search routes by cities and date
  searchRoutes: (searchData) => {
    const api = new ApiService();
    return api.post('/route/search', searchData);
  }
};

// RouteCity API methods
export const routeCityApi = {
  // Get all route cities
  getRouteCities: () => {
    const api = new ApiService();
    return api.get('/routecity');
  },

  // Get route cities by route ID
  getRouteCitiesByRoute: (routeId) => {
    const api = new ApiService();
    return api.get(`/routecity/route/${routeId}`);
  },

  // Get route city by ID
  getRouteCity: (id) => {
    const api = new ApiService();
    return api.get(`/routecity/${id}`);
  },

  // Create new route city
  createRouteCity: (routeCityData) => {
    const api = new ApiService();
    return api.post('/routecity', routeCityData);
  },

  // Update existing route city
  updateRouteCity: (id, routeCityData) => {
    const api = new ApiService();
    return api.put(`/routecity/${id}`, routeCityData);
  },

  // Delete route city (soft delete)
  deleteRouteCity: (id) => {
    const api = new ApiService();
    return api.delete(`/routecity/${id}`);
  },

              // Toggle route city status
            toggleStatus: (id) => {
                const api = new ApiService();
                return api.patch(`/routecity/${id}/toggle-status`);
            },
            
            // Get next available order ID for a route
            getNextOrderId: (routeId) => {
                const api = new ApiService();
                return api.get(`/routecity/route/${routeId}/next-order-id`);
            }
};

// Schedule API methods
export const scheduleApi = {
  // Get all schedules
  // Optional date parameter to filter schedules that have ScheduleDates for that date
  getSchedules: (date = null) => {
    const api = new ApiService();
    const params = date ? { date: date } : {};
    return api.get('/schedule', params);
  },

  // Get schedule by ID
  getSchedule: (id) => {
    const api = new ApiService();
    return api.get(`/schedule/${id}`);
  },

  // Create new schedule
  createSchedule: (scheduleData) => {
    const api = new ApiService();
    return api.post('/schedule', scheduleData);
  },

  // Update existing schedule
  updateSchedule: (id, scheduleData) => {
    const api = new ApiService();
    return api.put(`/schedule/${id}`, scheduleData);
  },

  // Delete schedule (soft delete)
  deleteSchedule: (id) => {
    const api = new ApiService();
    return api.delete(`/schedule/${id}`);
  },

  // Toggle schedule status
  toggleStatus: (id) => {
    const api = new ApiService();
    return api.patch(`/schedule/${id}/toggle-status`);
  },

  // Search schedules by date and route
  searchSchedules: (searchData) => {
    const api = new ApiService();
    return api.post('/schedule/search', searchData);
  },

  // Schedule Date methods
  // Get all schedule dates
  getScheduleDates: () => {
    const api = new ApiService();
    return api.get('/scheduledate');
  },

  // Get schedule dates by schedule ID
  // Optional date parameter to filter ScheduleDates for a specific date (performance optimization)
  getScheduleDatesBySchedule: (scheduleId, date = null) => {
    const api = new ApiService();
    const params = date ? { date: date } : {};
    return api.get(`/scheduledate/schedule/${scheduleId}`, params);
  },

  // Get schedule date by ID
  getScheduleDate: (id) => {
    const api = new ApiService();
    return api.get(`/scheduledate/${id}`);
  },

  // Create new schedule date
  createScheduleDate: (scheduleDateData) => {
    const api = new ApiService();
    return api.post('/scheduledate', scheduleDateData);
  },

  // Create bulk schedule dates
  createBulkScheduleDates: (scheduleId, scheduleDates) => {
    const api = new ApiService();
    return api.post(`/scheduledate/bulk?scheduleId=${scheduleId}`, scheduleDates);
  },

  // Update existing schedule date
  updateScheduleDate: (id, scheduleDateData) => {
    const api = new ApiService();
    return api.put(`/scheduledate/${id}`, scheduleDateData);
  },

  // Update schedule date status
  updateScheduleDateStatus: (id, status) => {
    const api = new ApiService();
    return api.put(`/scheduledate/${id}/status`, status);
  },

  // Toggle schedule date status
  toggleScheduleDateStatus: (id) => {
    const api = new ApiService();
    return api.patch(`/scheduledate/${id}/toggle-status`);
  },

  // Delete schedule date (soft delete)
  deleteScheduleDate: (id) => {
    const api = new ApiService();
    return api.delete(`/scheduledate/${id}`);
  }
};

// City API methods
export const cityApi = {
  // Get all cities
  getCities: (activeOnly = false) => {
    const api = new ApiService();
    const endpoint = activeOnly ? '/city/active' : '/city';
    return api.get(endpoint);
  },

  // Get city by ID
  getCity: (id) => {
    const api = new ApiService();
    return api.get(`/city/${id}`);
  },

  // Create new city
  createCity: (cityData) => {
    const api = new ApiService();
    return api.post('/city', cityData);
  },

  // Update existing city
  updateCity: (id, cityData) => {
    const api = new ApiService();
    return api.put(`/city/${id}`, cityData);
  },

  // Delete city
  deleteCity: (id) => {
    const api = new ApiService();
    return api.delete(`/city/${id}`);
  },

  // Toggle city status
  toggleStatus: (id) => {
    const api = new ApiService();
    return api.patch(`/city/${id}/toggle-status`);
  },

  // Check if city name is unique
  checkCityName: (cityName, excludeId = null) => {
    const api = new ApiService();
    const params = excludeId ? { excludeId } : {};
    return api.get(`/city/check-name/${cityName}`, params);
  }
};

// Customer API methods
export const customerApi = {
  // Get all customers
  getCustomers: (activeOnly = false) => {
    const api = new ApiService();
    const params = activeOnly ? { activeOnly: true } : {};
    return api.get('/customer', params);
  },

  // Get customer by ID
  getCustomer: (id) => {
    const api = new ApiService();
    return api.get(`/customer/${id}`);
  },

  // Get customer by phone
  getCustomerByPhone: (phone) => {
    const api = new ApiService();
    return api.get(`/customer/phone/${phone}`);
  },

  // Create customer
  createCustomer: (customerData) => {
    const api = new ApiService();
    return api.post('/customer', customerData);
  },

  // Find or create customer
  findOrCreateCustomer: (customerData) => {
    const api = new ApiService();
    return api.post('/customer/find-or-create', customerData);
  },

  // Update customer
  updateCustomer: (id, customerData) => {
    const api = new ApiService();
    return api.put(`/customer/${id}`, customerData);
  },

  // Delete customer
  deleteCustomer: (id) => {
    const api = new ApiService();
    return api.delete(`/customer/${id}`);
  }
};

// Booking API methods
export const bookingApi = {
  // Get bookings filtered by phone (for passengers)
  // fromDate and toDate are required by backend
  getBookings: (phone = null, fromDate = null, toDate = null) => {
    const api = new ApiService();
    const params = {};
    
    if (phone) {
      params.phone = phone;
    }
    
    // Add date parameters if provided
    if (fromDate) {
      params.fromDate = fromDate;
    }
    
    if (toDate) {
      params.toDate = toDate;
    }
    
    return api.get('/booking', params);
  },

  // Get all bookings without phone filter (for managers/admin)
  // Backend should handle missing phone parameter for manager/admin roles
  // Optional filters: fromDate, toDate, busNumber (vehicle registration number)
  getAllBookings: (filters = {}) => {
    const api = new ApiService();
    const params = {};
    
    // Add filter parameters if provided (only add non-empty values)
    if (filters.fromDate && typeof filters.fromDate === 'string' && filters.fromDate.trim() !== '') {
      params.fromDate = filters.fromDate.trim(); // Format: YYYY-MM-DD
    }
    if (filters.toDate && typeof filters.toDate === 'string' && filters.toDate.trim() !== '') {
      params.toDate = filters.toDate.trim(); // Format: YYYY-MM-DD
    }
    // Only add busNumber if it has a non-empty value
    // When "All Buses" is selected, filterBusNumber is empty string "", so don't send it at all
    // This prevents backend validation errors for empty busNumber
    if (filters.busNumber && typeof filters.busNumber === 'string' && filters.busNumber.trim() !== '') {
      params.busNumber = filters.busNumber.trim(); // Vehicle registration number
    }
    // Explicitly do NOT add busNumber if it's empty/null/undefined
    // The ApiService.get() method will also filter out empty strings, but this is an extra safeguard
    
    if (filters.busId && filters.busId > 0) {
      params.busId = filters.busId; // Vehicle ID (alternative to busNumber)
    }
    if (filters.bookingId && filters.bookingId > 0) {
      params.bookingId = filters.bookingId; // Booking ID
    }
    
    // Call /booking with filter parameters
    // Backend should check if user is manager/admin and return filtered bookings
    // busNumber will only be in params if it has a value, otherwise it's omitted completely
    return api.get('/booking', params);
  },

  // Get today's booking statistics for dashboard (no phone required for managers)
  // Returns: { todayBookingsCount: number, todayRevenue: number, date: string }
  getTodayDashboardStats: () => {
    const api = new ApiService();
    // Separate endpoint for dashboard - returns today's bookings count and revenue
    // Full URL will be: {API_URL}/booking/dashboard/today
    return api.get('/booking/dashboard/today');
  },

  // Get booking by ID
  getBooking: (id) => {
    const api = new ApiService();
    return api.get(`/booking/${id}`);
  },

  // Create booking
  createBooking: (bookingData) => {
    const api = new ApiService();
    return api.post('/booking', bookingData);
  },

  // Add booking seat (create BookingSeat record)
  addBookingSeat: (bookingId, bookingSeatData) => {
    const api = new ApiService();
    return api.post(`/booking/${bookingId}/seats`, bookingSeatData);
  },

  // Update booking status
  updateBookingStatus: (id, status) => {
    const api = new ApiService();
    return api.put(`/booking/${id}/status`, status);
  },

  // Update payment status
  updatePaymentStatus: (id, paymentStatus) => {
    const api = new ApiService();
    return api.put(`/booking/${id}/payment`, paymentStatus);
  },

  // Cancel booking
  cancelBooking: (id) => {
    const api = new ApiService();
    return api.delete(`/booking/${id}`);
  },

  // Admin cancel booking with remark (Admin/Manager only)
  adminCancelBooking: (id, remark) => {
    const api = new ApiService();
    return api.put(`/booking/${id}/cancel`, { remark: remark || '' });
  },

  // Get booking receipt PDF
  getBookingReceipt: async (id) => {
    const api = new ApiService();
    const token = localStorage.getItem('token');
    const headers = {
      'Accept': 'application/pdf',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${api.baseURL}/booking/${id}/receipt`, {
      method: 'GET',
      mode: 'cors',
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    return blob;
  }
};

// Auth API methods
export const authApi = {
  // Password login for Manager and Conductor
  login: (username, password) => {
    const api = new ApiService();
    return api.post('/auth/login', { username, password });
  },

  // Send OTP (for Passenger)
  sendOtp: (phone) => {
    const api = new ApiService();
    return api.post('/auth/send-otp', { phone });
  },

  // Verify OTP and login (for Passenger)
  verifyOtp: (phone, otp, name) => {
    const api = new ApiService();
    return api.post('/auth/verify-otp', { phone, otp, name });
  }
};

// Payment API methods
export const paymentApi = {
  // Initialize payment session
  initializePayment: (data) => {
    const api = new ApiService();
    return api.post('/payment/initialize', data);
  },

  // Verify payment
  verifyPayment: (data) => {
    const api = new ApiService();
    return api.post('/payment/verify', data);
  }
};

// Seat API methods
export const seatApi = {
  // Find or create seat
  findOrCreateSeat: (seatData) => {
    const api = new ApiService();
    return api.post('/seat/find-or-create', seatData);
  }
};

export default ApiService;
